import React, { useEffect, useMemo, useRef, useState } from "react";
import "./DownloadsDownloads.css";
import { FiSearch, FiSliders, FiArrowDown, FiDownload } from "react-icons/fi";
import pdfIcon from "../../../../assets/images/allsolutions/downloads/pdf.png";
import { useTranslation } from "react-i18next";
import JSZip from "jszip";
import { fetchAllDownloadsDocuments } from "../../../../utils/downloadsDocuments";
const SOURCE_FILTERS = ["all", "boards", "metals", "accessories", "solutions"];

const DownloadsDownloads = () => {
  const { i18n } = useTranslation();
  const searchInputRef = useRef(null);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("default");
  const [visibleCount, setVisibleCount] = useState(9);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const merged = await fetchAllDownloadsDocuments(i18n.language || "en");
        if (!isMounted) return;
        setDocuments(merged);
      } catch {
        if (isMounted) setDocuments([]);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  const filteredDocuments = useMemo(() => {
    return documents.filter((item) => {
      const matchesSearch = item.title.toLowerCase().includes(search.toLowerCase());
      const matchesSource = sourceFilter === "all" ? true : item.source === sourceFilter;
      return matchesSearch && matchesSource;
    });
  }, [documents, search, sourceFilter]);

  const sortedFilteredDocuments = useMemo(() => {
    const list = [...filteredDocuments];
    if (sortBy === "nameAsc") {
      list.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" }));
    } else if (sortBy === "nameDesc") {
      list.sort((a, b) => b.title.localeCompare(a.title, undefined, { sensitivity: "base" }));
    }
    return list;
  }, [filteredDocuments, sortBy]);

  const visibleDocuments = sortedFilteredDocuments.slice(0, visibleCount);
  const selectedDocuments = useMemo(
    () => documents.filter((doc) => selectedItems.includes(doc.id) && doc.fileUrl),
    [documents, selectedItems]
  );

  const sortLabel = sortBy === "default" ? "Latest" : sortBy === "nameAsc" ? "A–Z" : "Z–A";
  const sourceLabel =
    sourceFilter === "all"
      ? "All"
      : sourceFilter === "boards"
      ? "Boards"
      : sourceFilter === "metals"
      ? "Metals"
      : sourceFilter === "accessories"
      ? "Accessories"
      : "Solutions";

  const handleCycleSort = () => {
    setSortBy((prev) => (prev === "default" ? "nameAsc" : prev === "nameAsc" ? "nameDesc" : "default"));
  };

  const handleFocusSearch = () => {
    searchInputRef.current?.focus();
  };
  const handleCycleSourceFilter = () => {
    setSourceFilter((prev) => {
      const currentIndex = SOURCE_FILTERS.indexOf(prev);
      const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % SOURCE_FILTERS.length;
      return SOURCE_FILTERS[nextIndex];
    });
  };
  const allVisibleSelected =
    visibleDocuments.length > 0 &&
    visibleDocuments.every((item) => selectedItems.includes(item.id));

  const handleToggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedItems((prev) =>
        prev.filter((id) => !visibleDocuments.some((item) => item.id === id))
      );
      return;
    }

    setSelectedItems((prev) => [
      ...new Set([...prev, ...visibleDocuments.map((item) => item.id)]),
    ]);
  };

  const handleToggleItem = (documentId) => {
    setSelectedItems((prev) =>
      prev.includes(documentId)
        ? prev.filter((id) => id !== documentId)
        : [...prev, documentId]
    );
  };

  const handleLoadMore = () => {
    if (isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);

    window.setTimeout(() => {
      setVisibleCount((prev) => prev + 3);
      setIsLoadingMore(false);
    }, 650);
  };

  const handleOpenDownloadRequest = (documentItem) => {
    if (!documentItem?.fileUrl) return;
    window.open(documentItem.fileUrl, "_blank", "noopener,noreferrer");
  };

  const sanitizeFileName = (value) =>
    String(value || "document")
      .replace(/[\\/:*?"<>|]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const triggerBlobDownload = (blob, fileName) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleDownloadSelected = async () => {
    if (!selectedDocuments.length || isBulkDownloading) return;
    setIsBulkDownloading(true);
    try {
      if (selectedDocuments.length === 1) {
        const single = selectedDocuments[0];
        const singleResponse = await fetch(single.fileUrl);
        if (!singleResponse.ok) {
          throw new Error(`Failed to fetch "${single.title}"`);
        }
        const singleBlob = await singleResponse.blob();
        triggerBlobDownload(singleBlob, `${sanitizeFileName(single.title)}.pdf`);
        return;
      }

      const zip = new JSZip();
      const failed = [];

      await Promise.all(
        selectedDocuments.map(async (doc, index) => {
          try {
            const response = await fetch(doc.fileUrl);
            if (!response.ok) {
              throw new Error("not ok");
            }
            const blob = await response.blob();
            zip.file(`${sanitizeFileName(doc.title || `document-${index + 1}`)}.pdf`, blob);
          } catch {
            failed.push(doc.title || `Document ${index + 1}`);
          }
        })
      );

      const zipEntries = Object.keys(zip.files);
      if (!zipEntries.length) {
        throw new Error("No files could be downloaded for ZIP.");
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      triggerBlobDownload(zipBlob, "selected-downloads.zip");

      if (failed.length) {
        window.alert(`Some files could not be added to ZIP:\n- ${failed.join("\n- ")}`);
      }
    } catch {
      window.alert("Download selected failed. Please try again.");
    } finally {
      setIsBulkDownloading(false);
    }
  };

  return (
    <section className="downloadsDocs">
      <div className="downloadsDocs-toolbar">
        <div className="downloadsDocs-selectedWrap">
          <label className="downloadsDocs-selected">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={handleToggleAllVisible}
              aria-label="Select visible documents"
            />
            <span>{selectedItems.length} selected</span>
          </label>
          <button
            type="button"
            className="downloadsDocs-controlBtn"
            onClick={handleDownloadSelected}
            disabled={!selectedDocuments.length || isBulkDownloading}
            aria-label="Download selected files"
          >
            <FiDownload />
            <span>{isBulkDownloading ? "Preparing..." : "Download Selected"}</span>
          </button>
        </div>

        <div className="downloadsDocs-controls">
          <label className="downloadsDocs-search">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search files"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Search files"
            />
            <FiSearch />
          </label>

          <button type="button" className="downloadsDocs-controlBtn" onClick={handleCycleSourceFilter} aria-label="Change source filter">
            <FiSliders />
            <span>
              Filter: <strong>{sourceLabel}</strong>
            </span>
          </button>

          <button type="button" className="downloadsDocs-controlBtn" onClick={handleCycleSort} aria-label="Change sort order">
            <FiArrowDown />
            <span>
              Sort by: <strong>{sortLabel}</strong>
            </span>
          </button>
        </div>
      </div>

      <div className="downloadsDocs-table">
        {!visibleDocuments.length ? (
          <div className="downloadsDocs-row">
            <div className="downloadsDocs-col downloadsDocs-col--name">
              <span className="downloadsDocs-name">No downloads found.</span>
            </div>
            <div className="downloadsDocs-col downloadsDocs-col--date">-</div>
            <div className="downloadsDocs-col downloadsDocs-col--type">-</div>
            <div />
          </div>
        ) : visibleDocuments.map((item) => {
          const isChecked = selectedItems.includes(item.id);

          return (
            <div className="downloadsDocs-row" key={item.id}>
              <div className="downloadsDocs-col downloadsDocs-col--name">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => handleToggleItem(item.id)}
                  aria-label={`Select ${item.title}`}
                />
                <img src={pdfIcon} alt="PDF file" className="downloadsDocs-pdf" />
                <span className="downloadsDocs-name">{item.title}</span>
              </div>

              <div className="downloadsDocs-col downloadsDocs-col--date">
                {item.uploadedAt || "-"}
              </div>

              <div className="downloadsDocs-col downloadsDocs-col--type">
                {item.type}
              </div>

              <button
                type="button"
                className="downloadsDocs-downloadBtn"
                aria-label={`Download ${item.title}`}
                onClick={() => handleOpenDownloadRequest(item)}
                disabled={!item.fileUrl}
              >
                <FiDownload />
              </button>
            </div>
          );
        })}
      </div>

      {visibleCount < sortedFilteredDocuments.length && (
        <div className="downloadsDocs-loadMoreWrap">
          <button
            type="button"
            className="downloadsDocs-loadMore"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
          >
            <span>Load more</span>
            {isLoadingMore && (
              <span className="downloadsDocs-loadCircle" aria-hidden="true" />
            )}
          </button>
        </div>
      )}

    </section>
  );
};

export default DownloadsDownloads;